"use client";

import { useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { arCalendar } from "@/lib/datepicker-ar";
import "react-datepicker/dist/react-datepicker.css";
import Riyal from "@/components/ui/Riyal";
import PriceTag, { type Discounted } from "@/components/ui/PriceTag";
import { usePricingRates } from "@/hooks/usePublicData";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { computeCharges, fmtMoney } from "@/lib/pricing";
import { priceOn, stayPrices, sumPrices, uniformPrice } from "@/lib/nightly";

registerLocale("ar", arCalendar); // one-letter weekday headers (fit phones)

function Icon({
  name,
  className = "",
  fill = false,
}: {
  name: string;
  className?: string;
  fill?: boolean;
}) {
  return (
    <span
      className={`material-symbols-outlined ${fill ? "fill" : ""} ${className}`}
    >
      {name}
    </span>
  );
}

const nightsBetween = (a: Date | null, b: Date | null) =>
  a && b ? Math.max(0, Math.round((b.getTime() - a.getTime()) / 86400000)) : 0;

const ymdLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

export interface Availability {
  available: boolean;
  closedNights: string[];
  message: string;
  error?: boolean;
}

/** Live "are these dates still bookable?" (incl. Zaaer) — before the guest goes to checkout. */
export function useStayAvailability(propertyId: string | undefined, checkIn: string, checkOut: string) {
  return useQuery({
    queryKey: ["availability", propertyId, checkIn, checkOut],
    enabled: !!propertyId && !!checkIn && !!checkOut && checkOut > checkIn,
    staleTime: 30_000,
    retry: 1,
    queryFn: async (): Promise<Availability> => {
      const r = await fetch(
        `/api/v1/public/properties/${encodeURIComponent(propertyId!)}/availability?checkIn=${checkIn}&checkOut=${checkOut}`,
        { cache: "no-store" },
      );
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return { available: r.status !== 404, closedNights: [], message: j?.message || "", error: true };
      return j.data;
    },
  });
}

export default function BookingCard({
  propertyId,
  embedded = false,
  price,
  nightlyPrices,
  deal,
  maxGuests,
  isAr,
  whatsapp,
  addOnsTotal = 0,
  onReserve,
  loading = false,
  excludeDates = [],
  defaultCheckIn = null,
  defaultCheckOut = null,
  defaultGuests = 1,
}: {
  /** for the live availability check */
  propertyId?: string;
  /** inside a sheet / dialog (no sticky, no shadow) */
  embedded?: boolean;
  price: number;
  /** Price of each upcoming night (Zaaer OTA daily price) — nights vary. */
  nightlyPrices?: Record<string, number>;
  /** The unit's public discount: struck-through price + "-30%" (from the unit API). */
  deal?: Discounted & { nightlyBasePrices?: Record<string, number> };
  maxGuests: number;
  isAr: boolean;
  whatsapp?: string;
  addOnsTotal?: number;
  onReserve: (d: { checkIn: Date; checkOut: Date; guests: number }) => void;
  loading?: boolean;
  excludeDates?: Date[];
  defaultCheckIn?: Date | null;
  defaultCheckOut?: Date | null;
  defaultGuests?: number;
}) {
  const t = (a: string, e: string) => (isAr ? a : e);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [start, end] = range;
  const [guests, setGuests] = useState(1);
  const [showCal, setShowCal] = useState(embedded);
  const qc = useQueryClient();

  // Apply the search prefill (dates + guests) once, when it first arrives —
  // without clobbering the user's own later edits.
  const appliedPrefill = useRef(false);
  useEffect(() => {
    if (appliedPrefill.current) return;
    if (defaultCheckIn || defaultCheckOut || defaultGuests > 1) {
      if (defaultCheckIn || defaultCheckOut)
        setRange([defaultCheckIn, defaultCheckOut]);
      if (defaultGuests > 1) setGuests(defaultGuests);
      appliedPrefill.current = true;
    }
  }, [defaultCheckIn, defaultCheckOut, defaultGuests]);

  // Live availability of the picked stay (catches a booking made elsewhere
  // a minute ago — before the guest fills the checkout form).
  const ci = start && end ? ymdLocal(start) : "";
  const co = start && end ? ymdLocal(end) : "";
  const { data: avail, isFetching: checking } = useStayAvailability(propertyId, ci, co);
  const unavailable = !!avail && !avail.available;
  useEffect(() => {
    // The calendar learns the nights that turned out to be taken.
    if (unavailable && propertyId) qc.invalidateQueries({ queryKey: ["blocked-dates", propertyId] });
  }, [unavailable, propertyId, qc]);

  // Fast lookup of unavailable days (for red styling + tooltip).
  const blockedKeys = new Set([...excludeDates.map(ymdLocal), ...(avail?.closedNights || [])]);
  const isBlocked = (d: Date) => blockedKeys.has(ymdLocal(d));

  const nights = nightsBetween(start, end);
  // Each night at its own price — the server charges exactly this.
  const priced = { pricePerNight: price, nightlyPrices };
  const nightPrices = stayPrices(priced, start, end);
  const samePrice = uniformPrice(nightPrices);
  const subtotal = sumPrices(nightPrices);
  // Same nights before the discount → "you save X".
  const baseSubtotal = deal?.nightlyBasePrices ? sumPrices(stayPrices({ pricePerNight: price, nightlyPrices: deal.nightlyBasePrices }, start, end)) : subtotal;
  const saved = Math.round((baseSubtotal - subtotal) * 100) / 100;
  const hasVaryingPrices = !!nightlyPrices && new Set(Object.values(nightlyPrices).slice(0, 120)).size > 1;
  // Service fee + VAT on top (dashboard-managed rates; the server charges the same).
  const { data: rates } = usePricingRates();
  const charges = computeCharges(subtotal, 0, rates);
  const total = charges.totalPrice + (nights > 0 ? addOnsTotal : 0);

  const fmt = (d: Date | null) =>
    d
      ? d.toLocaleDateString(isAr ? "ar-EG" : "en-GB", {
          day: "numeric",
          month: "short",
        })
      : t("أضف", "Add");

  const handleReserve = () => {
    if (start && end && !unavailable && !checking) onReserve({ checkIn: start, checkOut: end, guests });
    else if (!start || !end || unavailable) setShowCal(true);
  };

  return (
    <div
      className={
        embedded
          ? "bg-surface"
          : "sticky top-25 border border-clay-100 rounded-3xl bg-surface p-6 md:p-8 shadow-xl"
      }
    >
      {/* Price + rating */}
      <div className="flex justify-between items-center mb-6">
        <PriceTag p={{ ...deal, pricePerNight: price }} isAr={isAr} priceClassName="font-display text-2xl font-bold text-ink-900" />
        <div className="flex items-center gap-1 font-bold text-sm text-ink-900">
          <Icon name="star" fill className="text-gold-500 text-[18px]" />
          {t("جديد", "New")}
        </div>
      </div>

      {/* Date + guests box */}
      <div className="border border-clay-100 rounded-xl overflow-hidden mb-4">
        <button
          type="button"
          onClick={() => setShowCal((v) => !v)}
          className="flex w-full border-b border-clay-100 hover:bg-clay-50 transition-colors text-start"
        >
          <div className="w-1/2 p-3 border-e border-clay-100">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-ink-500">
              {t("وصول", "Check-in")}
            </span>
            <span className="text-sm text-ink-900">{fmt(start)}</span>
          </div>
          <div className="w-1/2 p-3">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-ink-500">
              {t("مغادرة", "Check-out")}
            </span>
            <span className="text-sm text-ink-900">{fmt(end)}</span>
          </div>
        </button>
        <div className="p-3 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
            {t("الضيوف", "Guests")}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setGuests((n) => Math.max(1, n - 1))}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-clay-100 text-ink-900 hover:border-clay-600 hover:text-clay-600"
              aria-label="-"
            >
              −
            </button>
            <span className="min-w-4 text-center text-sm font-semibold text-ink-900">
              {guests}
            </span>
            <button
              type="button"
              onClick={() => setGuests((n) => Math.min(maxGuests || 30, n + 1))}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-clay-100 text-ink-900 hover:border-clay-600 hover:text-clay-600"
              aria-label="+"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {showCal && (
        <div className="mb-4 flex justify-center">
          <DatePicker
            selected={start}
            onChange={(dates: [Date | null, Date | null]) => {
              setRange(dates);
              if (dates[0] && dates[1]) setShowCal(false);
            }}
            startDate={start}
            endDate={end}
            selectsRange
            minDate={new Date()}
            excludeDates={[...excludeDates, ...(avail?.closedNights || []).map((n) => new Date(`${n}T00:00:00`))]}
            dayClassName={(d) => (isBlocked(d) ? "najdi-day-blocked" : "")}
            renderDayContents={(day, d) =>
              d && isBlocked(d) ? (
                <span title={t("اليوم غير متاح", "Unavailable")}>{day}</span>
              ) : hasVaryingPrices && d && d >= new Date(new Date().toDateString()) ? (
                <span>
                  {day}
                  <span className="najdi-day-price">{Math.round(priceOn(priced, ymdLocal(d))).toLocaleString("en")}</span>
                </span>
              ) : (
                <span>{day}</span>
              )
            }
            locale={isAr ? "ar" : undefined}
            calendarClassName={`najdi-calendar${hasVaryingPrices ? " najdi-priced" : ""}`}
            inline
          />
        </div>
      )}
      {end && start && (
        <div className="-mt-1 mb-4" aria-live="polite">
          {checking ? (
            <p className="flex items-center justify-center gap-1.5 text-xs text-ink-500">
              <Icon name="progress_activity" className="animate-spin text-[14px]" />
              {t("جارٍ التحقق من توفر التواريخ…", "Checking availability…")}
            </p>
          ) : unavailable ? (
            <div className="flex items-start gap-2 rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2.5 text-xs font-semibold leading-relaxed text-red-700">
              <Icon name="event_busy" className="text-[18px]" />
              <span>{avail?.message || t("التواريخ غير متاحة — اختر تواريخ أخرى", "These dates aren't available — pick others")}</span>
            </div>
          ) : avail?.error ? (
            <p className="flex items-center justify-center gap-1.5 text-xs text-ink-500">
              <Icon name="info" className="text-[14px]" />
              {t("سنتأكد من التوفر عند تأكيد الحجز", "We'll confirm availability at booking")}
            </p>
          ) : (
            <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700">
              <Icon name="event_available" className="text-[14px]" />
              {t("التواريخ متاحة للحجز", "Selected dates are available")}
            </p>
          )}
        </div>
      )}

      {/* Reserve */}
      <button
        onClick={() => handleReserve()}
        disabled={loading || (!!start && !!end && (checking || unavailable))}
        className="w-full bg-clay-600 text-white py-4 rounded-xl font-bold text-lg mb-3 cursor-pointer hover:bg-clay-700 hover:shadow-lg transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Icon name="progress_activity" className="animate-spin" />
            {t("جارٍ الحجز…", "Reserving…")}
          </>
        ) : (
          <>
            {start && end ? t("تأكيد الحجز", "Confirm booking") : t("اختر التواريخ", "Choose dates")}
            <Icon name={start && end ? "bolt" : "calendar_month"} />
          </>
        )}
      </button>
      <p className="text-center text-ink-500 text-sm mb-6">
        {t("لن يتم خصم أي مبالغ الآن", "You won't be charged yet")}
      </p>

      {/* Breakdown */}
      {nights > 0 && (
        <>
          <div className="flex flex-col gap-3 mb-4 border-b border-clay-100 pb-4">
            <div className="flex justify-between items-center text-ink-500 text-sm">
              <span className="underline underline-offset-4">
                {samePrice != null ? (
                  <>
                    {samePrice.toLocaleString()} <Riyal /> × {nights} {t("ليالٍ", "nights")}
                  </>
                ) : (
                  <>
                    {nights} {t("ليالٍ", "nights")} · {t("متوسط", "avg")} {fmtMoney(subtotal / nights)} <Riyal />
                  </>
                )}
              </span>
              <span>
                {fmtMoney(subtotal)} <Riyal />
              </span>
            </div>
            {saved > 0 && (
              <div className="flex justify-between items-center text-sm font-semibold text-green-700">
                <span>
                  {t("شامل خصم", "Includes")} {deal?.discount?.percent}%{isAr ? "" : " off"}
                </span>
                <span>
                  {t("وفّرت", "You save")} {fmtMoney(saved)} <Riyal />
                </span>
              </div>
            )}
            {charges.serviceFee > 0 && (
              <div className="flex justify-between items-center text-ink-500 text-sm">
                <span>{t("رسوم الخدمة", "Service fee")} ({rates?.serviceFeePercent}%)</span>
                <span>
                  {fmtMoney(charges.serviceFee)} <Riyal />
                </span>
              </div>
            )}
            {charges.vatAmount > 0 && (
              <div className="flex justify-between items-center text-ink-500 text-sm">
                <span>{t("ضريبة القيمة المضافة", "VAT")} ({rates?.vatPercent}%)</span>
                <span>
                  {fmtMoney(charges.vatAmount)} <Riyal />
                </span>
              </div>
            )}
            {addOnsTotal > 0 && (
              <div className="flex justify-between items-center text-ink-500 text-sm">
                <span className="underline underline-offset-4">
                  {t("الخدمات الإضافية", "Add-on services")}
                </span>
                <span>
                  {addOnsTotal.toLocaleString()} <Riyal />
                </span>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center font-bold text-lg mb-6 text-ink-900">
            <span>{t("الإجمالي", "Total")}</span>
            <span>
              {fmtMoney(total)} <Riyal />
            </span>
          </div>
        </>
      )}

      {/* WhatsApp */}
      {whatsapp && (
        <a
          href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full border-2 border-clay-600/20 text-clay-600 py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-clay-50 transition-all"
        >
          <Icon name="chat" />
          {t("تواصل عبر الواتساب", "Chat on WhatsApp")}
        </a>
      )}
    </div>
  );
}
